<template>
  <CRow>
    <CCol md="6">
      <div class="form-group">
        <div class="lista-dupla">
          <div class="lista-dupla__cabecalho">
            <span>{{ tituloDisponiveis }}</span>
            <span class="lista-dupla__contador">
              {{ disponiveis.length }}
            </span>
          </div>
          <ul class="lista-dupla__itens" :style="{ height: altura }">
            <li
              v-for="item in disponiveis"
              v-bind:key="idDe(item)"
              class="lista-dupla__item"
              v-on:click="vincular(idDe(item))"
              :title="'Vincular ' + textoDe(item)"
            >
              <span class="lista-dupla__texto">
                <strong v-if="campoDestaque">{{ item[campoDestaque] }}</strong>
                {{ textoDe(item) }}
              </span>
              <fa-icon icon="plus" class="lista-dupla__acao" />
            </li>
            <li v-if="!disponiveis.length" class="lista-dupla__vazio">
              {{ textoVazioDisponiveis }}
            </li>
          </ul>
        </div>
      </div>
    </CCol>
    <CCol md="6">
      <div class="form-group">
        <div class="lista-dupla lista-dupla--vinculadas">
          <div class="lista-dupla__cabecalho">
            <span>{{ tituloSelecionados }}</span>
            <span
              class="lista-dupla__limpar"
              v-if="selecionados.length"
              v-on:click="limpar()"
            >
              Limpar
            </span>
            <span v-else class="lista-dupla__contador">0</span>
          </div>
          <ul class="lista-dupla__itens" :style="{ height: altura }">
            <li
              v-for="item in selecionados"
              v-bind:key="idDe(item)"
              class="lista-dupla__item"
              v-on:click="desvincular(idDe(item))"
              :title="'Remover ' + textoDe(item)"
            >
              <span class="lista-dupla__texto">
                <strong v-if="campoDestaque">{{ item[campoDestaque] }}</strong>
                {{ textoDe(item) }}
              </span>
              <fa-icon icon="times" class="lista-dupla__acao" />
            </li>
            <li v-if="!selecionados.length" class="lista-dupla__vazio">
              {{ textoVazioSelecionados }}
            </li>
          </ul>
        </div>
        <div v-if="mensagemErro" class="message">{{ mensagemErro }}</div>
      </div>
    </CCol>
  </CRow>
</template>

<script>
/**
 * Seleção por duas listas: disponíveis à esquerda, selecionados à direita, com
 * o item inteiro clicável. Padrão visual do cadastro de adquirentes.
 *
 * Aceita `items` como array ou como objeto indexado por chave — o TheContainer
 * entrega os sistemas nesse segundo formato.
 */
export default {
  name: "ListaDupla",
  props: {
    /** Ids selecionados (v-model). */
    value: {
      type: Array,
      default: () => [],
    },
    /** Universo de itens disponíveis para vincular. */
    items: {
      type: [Array, Object],
      default: () => [],
    },
    tituloDisponiveis: {
      type: String,
      default: "Disponíveis",
    },
    tituloSelecionados: {
      type: String,
      default: "Selecionados",
    },
    /** Campo que identifica o item. */
    campoId: {
      type: String,
      default: "id",
    },
    /** Campo exibido em negrito antes do texto (opcional). */
    campoDestaque: {
      type: String,
      default: "",
    },
    /** Campo com o texto principal do item. */
    campoTexto: {
      type: String,
      required: true,
    },
    textoVazioDisponiveis: {
      type: String,
      default: "Tudo já está vinculado",
    },
    textoVazioSelecionados: {
      type: String,
      default: "Clique nos itens à esquerda para vincular",
    },
    mensagemErro: {
      type: String,
      default: "",
    },
    altura: {
      type: String,
      default: "280px",
    },
  },
  computed: {
    lista() {
      return Array.isArray(this.items)
        ? this.items
        : Object.values(this.items || {});
    },
    disponiveis() {
      return this.lista.filter((item) => !this.estaSelecionado(this.idDe(item)));
    },
    /**
     * Na ordem do cadastro de origem, e não na ordem de clique.
     */
    selecionados() {
      return this.lista.filter((item) => this.estaSelecionado(this.idDe(item)));
    },
  },
  methods: {
    idDe(item) {
      return item[this.campoId];
    },
    textoDe(item) {
      return item[this.campoTexto];
    },
    /**
     * Comparação por texto de propósito: a API devolve os vínculos como string
     * ("1") e o cadastro de origem pode trazer o id como número. O v-model de
     * checkbox do Vue 2 usava `looseEqual`, que compara do mesmo jeito — com
     * igualdade estrita, item já vinculado abriria como não selecionado.
     */
    estaSelecionado(id) {
      return (this.value || []).some(
        (selecionado) => String(selecionado) === String(id)
      );
    },
    vincular(id) {
      if (!this.estaSelecionado(id)) {
        this.$emit("input", (this.value || []).concat(id));
      }
    },
    desvincular(id) {
      this.$emit(
        "input",
        (this.value || []).filter(
          (selecionado) => String(selecionado) !== String(id)
        )
      );
    },
    limpar() {
      this.$emit("input", []);
    },
  },
};
</script>

<style scoped>
/* Lista dupla: caixa arredondada, cabeçalho discreto e item inteiro clicável —
   o botãozinho por linha do padrão antigo ficava desalinhado no formulário. */
.lista-dupla {
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.lista-dupla__cabecalho {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  background: #f9fafb;
  border-bottom: 1px solid #e4e7ec;
  font-size: 11px;
  font-weight: 700;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.lista-dupla__contador {
  background: #eef0f5;
  color: #667085;
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
}

.lista-dupla__limpar {
  color: #d92d20;
  cursor: pointer;
  font-size: 11px;
  text-transform: none;
  letter-spacing: 0;
}

.lista-dupla__limpar:hover {
  text-decoration: underline;
}

.lista-dupla__itens {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
}

.lista-dupla__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 12px;
  font-size: 12px;
  color: #344054;
  border-bottom: 1px solid #f2f4f7;
  cursor: pointer;
  transition: background 0.12s ease;
}

.lista-dupla__item:last-child {
  border-bottom: none;
}

.lista-dupla__item:hover {
  background: #f7f7fc;
}

.lista-dupla--vinculadas .lista-dupla__item:hover {
  background: #fdf6f5;
}

.lista-dupla__texto strong {
  color: #667085;
  margin-right: 4px;
}

.lista-dupla__acao {
  color: #98a2b3;
  flex-shrink: 0;
}

.lista-dupla__item:hover .lista-dupla__acao {
  color: #3a3e81;
}

.lista-dupla--vinculadas .lista-dupla__item:hover .lista-dupla__acao {
  color: #d92d20;
}

.lista-dupla__vazio {
  padding: 16px 12px;
  font-size: 12px;
  color: #98a2b3;
  text-align: center;
}

.message {
  font-size: 11px;
  color: #d92d20;
  margin-top: 2px;
}
</style>
